import { useEffect, useState } from "react";
import { isAbortError, queryReport } from "../api/newReportingClient";
import { ReportQueryParams, ReportResponse } from "../api/types";

export interface ReportQueryState {
  data: ReportResponse | null;
  loading: boolean;
  error: string | null;
}

// Runs /reports/query whenever the params change. On any change (or unmount) the previous
// request is ABORTED via AbortController, so a slow stale response can neither overwrite newer
// data nor keep running server-side. The previous `data` is kept while the new request is in
// flight. Pass `null` to skip the fetch (e.g. before Search, or a disabled comparison series).
export function useReportQuery(params: ReportQueryParams | null): ReportQueryState {
  const [state, setState] = useState<ReportQueryState>({
    data: null,
    loading: false,
    error: null,
  });

  // Serialize params so the effect re-runs on any value change.
  const key = params ? JSON.stringify(params) : null;

  useEffect(() => {
    if (!params) {
      setState({ data: null, loading: false, error: null });
      return;
    }
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: null }));

    queryReport(params, controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted || isAbortError(err)) return; // cancelled — leave newer state alone
        const message = err instanceof Error ? err.message : "Failed to load report";
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
